<template>
  <div v-if="version === 'v1'" class="tw-mt-2 tw-grid tw-grid-cols-12 tw-gap-x-4 tw-gap-y-10 lg:tw-gap-y-12">
    
    <div
      v-for="(property, index) in properties"
      :key="String(property.property_profile_id)"
      role="link"
      class="d-flex flex-column mb-3 mb-md-0 n-cursor-pointer property-card tw-col-span-12 tw-border-b tw-border-[#CCCCCC] md:tw-col-span-6 xl:tw-col-span-4"
      @click="onCardClick(property.detail_page_url)"
    >
      <div class="tw-flex tw-flex-col">
        <div class="image-wrapper project property-image w-100">
          <library-image
            :key="property.property_profile_id"
            :src="property.image"
            :w-ratio="4"
            :h-ratio="3"
            :blur="imageBlurFilter(index)"
            loading="lazy"
            alt=""
            :quality="60"
            image-class="cover-image project property-img"
            style="height: 320px; object-fit: cover; object-position: center"
          />
          <div v-if="property.custom_property_status_title" class="property-image-badge">
            {{ property.custom_property_status_title }}
          </div>
        </div>
        <div>
          <template v-if="!property.is_locked">
            <div
              class="mt-2 p-4 d-flex flex-row justify-content-between align-items-center"
              style="background-color: var(--tertiary_color); color: var(--white); height: 100px"
            >
              <div class="w-100 d-flex flex-column">
                <div class="n-capitalized" style="font-size: 12px">
                  {{ property.price.type.name }}
                </div>
                <div class="d-flex w-100 justify-content-between align-items-center">
                  <div style="font-size: 24px">
                    {{ property.price.price }}
                  </div>
                  <div class="d-flex flex-row align-items-center" style="column-gap: 10px; position: relative">
                    <div
                      v-if="property.match_percentage"
                      style="font-size: 16px"
                      class="d-flex align-items-center pt-1"
                    >
                      {{ property.match_percentage }} MATCH
                    </div>
                    <nc-tooltip>
                      <template #item>
                        <span class="nc-icon-info-circle d-flex align-items-center" style="font-size: 20px"></span>
                      </template>
                      <template #tooltip>
                        <h4 class="mb-2 pt-2">Match Score:</h4>
                        <p>
                          This is a prediction of what our software thinks you may find an interesting property, based
                          on the unique search criteria that you gave us. You can improve the relevance of your match
                          score by adjusting and saving your search criteria.
                        </p>
                      </template>
                    </nc-tooltip>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <div
              class="mt-2 p-4 d-flex flex-row justify-content-between align-items-center"
              style="background-color: var(--tertiary_color); color: var(--white); height: 100px"
            >
              <h3 style="color: var(--white)" class="tw-text-xl tw-font-extralight">Pre-Market Property</h3>
            </div>
          </template>
        </div>
      </div>
      <template v-if="!property.is_locked">
        <div class="mt-3 d-flex flex-row align-items-start tw-h-[80px]">
          <h3 style="color: var(--tertiary_color)" class="tw-text-xl tw-font-extralight">
            {{ property.advert_heading }}
          </h3>
        </div>
        <div class="mt-3 d-flex flex-row align-items-start tw-h-[70px]">
          <div class="d-flex flex-column" style="color: var(--tertiary_color)">
            <div style="letter-spacing: 5px; font-weight: 100; font-size: 12px">ADDRESS</div>
            <div class="tw-mt-1 tw-text-sm tw-font-extralight">
              {{ property.display_address }}
            </div>
          </div>
        </div>
        <div class="mt-3 d-flex flex-row align-items-start tw-h-auto tw-pb-12 md:tw-h-[100px] md:tw-pb-0">
          <div class="d-flex flex-column" style="color: var(--tertiary_color)">
            <div style="letter-spacing: 5px; font-weight: 100; font-size: 12px">DESCRIPTION</div>
            <div class="tw-mt-1 tw-line-clamp-5 tw-text-sm tw-font-extralight">
              {{ property.summary }}
            </div>
          </div>
        </div>
        <div class="tw-mb-2 tw-mt-10 tw-flex tw-gap-x-2">
          <nuxt-link
            :to="property.detail_page_url"
            data-w-id="bd454024-605c-912a-bc49-318b72174c6f"
            class="n-primary button tw-w-fit"
            :external="true"
            >View This Property</nuxt-link
          >
        </div>
      </template>
      <template v-else>
        <div class="tw-flex tw-h-full tw-flex-col tw-justify-between">
          <h3
            style="color: var(--tertiary_color)"
            class="mt-3 d-flex flex-row align-items-start tw-h-[80px] tw-text-xl tw-font-extralight"
          >
            {{ property.advert_heading }}
          </h3>
          <div class="mt-3 d-flex flex-row align-items-start tw-h-[70px]">
            <div class="d-flex flex-column" style="color: var(--tertiary_color)">
              <div style="letter-spacing: 5px; font-weight: 100; font-size: 12px">ADDRESS</div>
              <div class="tw-mt-1 tw-text-sm tw-font-extralight">
                {{ property.display_address }}
              </div>
            </div>
          </div>

          <div class="mt-3 d-flex flex-row align-items-start tw-h-auto tw-pb-12 md:tw-h-[100px] md:tw-pb-0">
            <div class="d-flex flex-column" style="color: var(--tertiary_color)">
              <div style="letter-spacing: 5px; font-weight: 100; font-size: 12px">DESCRIPTION</div>
              <div class="tw-mt-1 tw-line-clamp-5 tw-text-sm tw-font-extralight">
                This is a pre-market property. You need to create an account and register to our property alerts in
                order to view it.
              </div>
            </div>
          </div>
          <div class="tw-my-2 tw-flex tw-gap-x-2">
            <nuxt-link
              :to="routes.REGISTER"
              data-w-id="bd454024-605c-912a-bc49-318b72174c6f"
              class="n-primary button tw-w-fit"
              :external="true"
              >Register</nuxt-link
            >
            <nuxt-link
              :to="`${routes.PROPERTY_DETAIL.replace(':propertyId', String(property.property_profile_id))}#login`"
              data-w-id="bd454024-605c-912a-bc49-318b72174c6f"
              class="n-primary button tw-w-fit"
              :external="true"
              >Login</nuxt-link
            >
          </div>
        </div>
      </template>
    </div>
  </div>

  <div v-else-if="version === 'v2'" class="tw-grid tw-grid-cols-12 tw-gap-y-10 lg:tw-gap-x-10 lg:tw-gap-y-12">
    <template
      v-for="(property, propertyIndex) in properties"
      :key="property.property_profile_id || `property-${propertyIndex}`"
    >
      <!-- Property card -->
      <PropertyListItem
        :component="component"
        :property="property"
        :index="propertyIndex"
        class="tw-col-span-12 lg:tw-col-span-6 xl:tw-col-span-4"
      />

      <!-- Custom card after every nth property -->
      <template v-for="(customcard, index) in cards" :key="`custom-card-${index}`">
        <div
          v-if="customcard.index === propertyIndex"
          :style="`background-color: var(--c-${customcard.background}); color: var(--c-${customcard.text}); border-radius: var(--rounded);`"
          class="tw-col-span-12 tw-flex tw-flex-col tw-items-start tw-justify-center tw-p-8 tw-text-start lg:tw-col-span-6 xl:tw-col-span-4"
        >
          <h6 v-if="customcard.alt_title" class="tw-mb-4 tw-uppercase tw-text-[var(--c-secondary)]">
            {{ customcard.alt_title }}
          </h6>
          <h4 v-if="customcard.title" class="tw-max-w-[350px] tw-uppercase">{{ customcard.title }}</h4>
          <p v-if="customcard.description" class="tw-mt-10 tw-max-w-[350px] tw-text-start">
            {{ customcard.description }}
          </p>
          <nuxt-link
            v-if="customcard.button_label"
            :to="customcard.button_link"
            class="tw-mt-10 tw-self-start"
            :class="`n-${customcard.button_type}`"
          >
            {{ customcard.button_label }}
            <span class="btn-arrow"
              ><svg width="9" height="15" viewBox="0 0 7 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M5.92455 7.41479L2.62464 10.7147L1.7998 9.88987L4.6873 7.00237L1.7998 4.11487L2.62464 3.29004L5.92455 6.58996C6.03391 6.69935 6.09535 6.84769 6.09535 7.00237C6.09535 7.15705 6.03391 7.3054 5.92455 7.41479Z"
                  fill="white"
                />
              </svg>
            </span>
          </nuxt-link>
        </div>
      </template>
    </template>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { ComponentMixin } from '~/mixins/component.mixin';
import type { PropertyItemComponent } from '~/units/properties/types';
import NcTooltip from '~/components/common/NcTooltip.vue';
import PropertyListItem from '~/components/theme2/properties/PropertyListItem.vue';
import { ROUTES } from '~/constants/ui.constants';
import { navigateTo } from '#app';

type CustomCard = {
  index: number;
  background: string;
  text: string;
  alt_title?: string;
  title?: string;
  description?: string;
  button_label?: string;
  button_link?: string;
  button_type?: string;
};

export default defineNuxtComponent({
  name: 'PropertiesListBox',
  components: { NcTooltip, PropertyListItem },

  mixins: [ComponentMixin],

  props: {
    properties: {
      required: true,
      type: Array as PropType<PropertyItemComponent[]>,
    },

    version: {
      default: 'v1',
      type: String,
    },

    cards: {
      required: false,
      type: Array as PropType<CustomCard[]>,
      default: () => [],
    },
  },

  computed: {
    routes() {
      return ROUTES;
    },
  },

  methods: {
    onCardClick(url: string) {
      // Use helper if available via mixin, fallback to navigateTo
      if (typeof this.redirectTo === 'function') {
        // Some mixins provide redirectTo(component)
        this.redirectTo(url, true);
      } else {
        navigateTo(url);
      }
    },
    imageBlurFilter(index: number) {
      if (this.properties && this.properties[index]?.is_locked) {
        return '14px';
      }
      return undefined;
    },
  },
});
</script>

<style scoped>
.property-card {
  transition:
    transform 0.3s linear,
    -webkit-transform 0.3s linear;
}
.property-card:hover {
  -webkit-transform: scale(1.02);
  transform: scale(1.02);
}

.property-image {
  position: relative;
}

.property-image-badge {
  position: absolute;
  top: 1rem;
  left: 1rem;
  background-color: var(--dark-brown);
  color: var(--white);
  padding: 10px;
  font-weight: bold;
}

.property-img::v-deep(.pre-market-property-badge) {
  font-size: 1.8rem;
}
</style>
